import type React from "react";
import { Group, Loader, Text } from "@mantine/core";
import { Badge, Table } from "@xplatform/design-system";
import { IconCornerDownRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { RowEdit } from "@models/job";
import type { MappingResultRowTm, TmTarget } from "@models/mappingResult";
import { buildCandidates } from "@utils/rowHelpers";
import AltText from "@components/AltText/AltText";

interface TmJobResultsExpandedRowProps {
  row: MappingResultRowTm;
  edit: RowEdit | undefined;
  /** Every candidate the server served for this row, rank 1 first. */
  candidates: readonly TmTarget[] | undefined;
  colSpan: number;
  disabled?: boolean;
  /** True while the candidates are still being fetched on demand. */
  loading?: boolean;
  onPickAlternative: (rank: number) => void;
}

/**
 * The candidate dropdown for one TM row.
 *
 * The list is rendered exactly as the server sent it — same order, nothing
 * dropped. Rank 1 is in it deliberately: after a correction it is the
 * pipeline's own top match, and picking it back is how the correction is
 * undone. It used to be sliced off here (and the rest capped at a client-side
 * number), which quietly removed the undo and hid candidates the backend had
 * served on purpose.
 *
 * Which entry is the row's current answer comes from `isCurrent`, computed
 * once in `buildCandidates`. Nothing here compares a rank to 1 — that
 * comparison stopped meaning "the row's answer" the moment anyone corrected a
 * row.
 */
const TmJobResultsExpandedRow: React.FC<TmJobResultsExpandedRowProps> = ({
  row,
  edit,
  candidates,
  colSpan,
  disabled = false,
  loading = false,
  onPickAlternative,
}) => {
  const { t } = useTranslation();
  const entries = buildCandidates(row, edit, candidates);

  if (entries.length === 0) {
    return (
      <Table.Tr className="job-results-page__alts-empty-row">
        <Table.Td colSpan={colSpan}>
          {loading ? (
            <Group justify="center" gap="xs">
              <Loader size="xs" />
              <Text size="sm" c="dimmed">
                {t("jobResultsPage.expand.loading", {
                  defaultValue: "Loading matches…",
                })}
              </Text>
            </Group>
          ) : (
            <Text size="sm" c="dimmed" ta="center">
              {t("jobResultsPage.expand.empty", {
                defaultValue: "No matches available",
              })}
            </Text>
          )}
        </Table.Td>
      </Table.Tr>
    );
  }

  const handlePick = (rank: number): void => {
    if (disabled) return;
    // Every row in this list is a PICK — including rank 1. Routing rank 1
    // through accept used to work when accept meant "take the top match"; now
    // that an accept SEALS the current pick, it would re-seal the candidate
    // the reviewer is trying to move away from.
    onPickAlternative(rank);
  };

  return (
    <>
      <Table.Tr className="job-results-page__alts-header-row">
        <Table.Td colSpan={colSpan}>
          <Text
            size="xs"
            fw={700}
            c="green"
            tt="uppercase"
            style={{ letterSpacing: "0.06em" }}
          >
            {t("jobResultsPage.expand.title", {
              count: entries.length,
              defaultValue: `Top ${entries.length} matches - click to pick`,
            })}
          </Text>
        </Table.Td>
      </Table.Tr>
      {entries.map(({ target, isCurrent }) => {
        const cls = `job-results-page__alt-row${
          isCurrent ? " job-results-page__alt-row--selected" : ""
        }${disabled ? " job-results-page__alt-row--disabled" : ""}`;
        return (
          <Table.Tr
            key={target.rank ?? target.key}
            className={cls}
            onClick={() => target.rank != null && handlePick(target.rank)}
            style={
              disabled
                ? { cursor: "wait", pointerEvents: "none", opacity: 0.5 }
                : undefined
            }
          >
            {/* # */}
            <Table.Td>
              <IconCornerDownRight
                size={14}
                className="job-results-page__alt-arrow"
              />
            </Table.Td>
            {/* Source path / Value — blank so a candidate's listing / id /
                value line up under the columns they belong to. */}
            <Table.Td />
            <Table.Td />
            <Table.Td>
              <Group gap={6} wrap="nowrap" align="flex-start">
                <AltText value={target.itemMpListing} />
              </Group>
            </Table.Td>
            <Table.Td>
              <AltText
                value={target.itemMpId == null ? null : String(target.itemMpId)}
                mono
              />
            </Table.Td>
            <Table.Td>
              <AltText value={target.valueDisplay} />
            </Table.Td>
            {/* Status */}
            <Table.Td>
              {isCurrent && (
                <Badge size="xs" variant="filled" color="green">
                  {t("jobResultsPage.expand.selected", {
                    defaultValue: "Selected",
                  })}
                </Badge>
              )}
            </Table.Td>
            <Table.Td />
            <Table.Td />
          </Table.Tr>
        );
      })}
    </>
  );
};

export default TmJobResultsExpandedRow;
